import { useTranslation } from 'react-i18next';
import { createPassword, getOrganisation } from './NonEmployeePassword.helpers';
import {
  Body,
  ButtonContainer,
  ButtonWrapper,
  Container,
  FooterContainer,
  Header,
  Heading,
  HeadingContainer,
  Info,
  Passwords,
  Submitted,
  textboxStyles,
} from './NonEmployeePassword.styles';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import PageNotFound from 'src/components/PageNotFound';
import { Button, Center, Link, Spinner } from 'src/components/atoms';
import ShadowContainer from 'src/components/atomsLegacy/containers/ShadowContainer';
import { Footer } from 'src/components/moleculesLegacy/Footer';
import { TextBox } from 'src/components/organismsLegacy/TextBox';
import CustomHeader from 'src/components/public/CustomHeader';
import { useTheme } from 'styled-components';
import { validatePassword } from 'src/validators';

interface Props {
  token: string;
  slug: string;
}

export const NonEmployeePassword: FC<Props> = ({ token, slug }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const theme = useTheme();
  useEffect(() => {
    getOrganisation(slug, setLoading, setNotFound);
  }, [slug]);

  useEffect(() => {
    if (password === '' || confirmPassword === '') {
      return setError('');
    }
    if (confirmPassword) {
      if (password !== confirmPassword) {
        return setError(`The password and confirmation fields have to match.`);
      }
      const message = validatePassword(password);
      if (message) {
        return setError(message);
      }
      return setError('');
    }
  }, [password, confirmPassword]);

  if (loading) {
    return (
      <Center>
        <Spinner variant="secondary" size="xlarge" />
      </Center>
    );
  }

  if (notFound) {
    return (
      <PageNotFound
        color={
          theme.isVaultVariant
            ? theme.newPalette.turquoise.T300
            : theme.atlas.semantic.color.action.primary.default
        }
      />
    );
  }

  return (
    <Container>
      <Header>
        <CustomHeader hideID />
      </Header>

      {!submitted && (
        <ShadowContainer loggedIn={false}>
          <HeadingContainer changePassword={false}>
            <Heading>{t(`public.Password.createPasswordHeader`)}</Heading>
          </HeadingContainer>
          <Body>
            <Info>
              <img
                alt="info icon"
                className="icon-image-text"
                src="/assets/icon_info_green.png"
              />
              <p
                className="public-reporting-detail start"
                style={{
                  margin: '15px 0',
                  fontSize: '12px',
                }}
              >
                {t('public.Password.description')}
              </p>
            </Info>
            <Passwords>
              <TextBox
                id="non-employee-password"
                data-testid="non-employee-password"
                password={true}
                style={textboxStyles}
                valid={password === '' || !validatePassword(password)}
                value={password}
                placeholder={t('public.Password.passwordPlaceholder')}
                onChange={(
                  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => setPassword(event.currentTarget.value)}
              />
              <TextBox
                id="non-employee-confirmation"
                data-testid="non-employee-confirmation"
                password={true}
                style={textboxStyles}
                valid={
                  confirmPassword === '' || !validatePassword(confirmPassword)
                }
                value={confirmPassword}
                placeholder={t('public.Password.reenterPlaceholder')}
                onChange={(
                  event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => setConfirmPassword(event.currentTarget.value)}
                enterKeyAction={async () => {
                  await createPassword(
                    password,
                    confirmPassword,
                    error,
                    token,
                    setSubmitted,
                  );
                }}
              />
              {error && (
                <p
                  className="public-reporting-detail start password-error"
                  style={{ margin: '15px 0' }}
                  data-testid="non-employee-password-error"
                >
                  {error}
                </p>
              )}
            </Passwords>
          </Body>
          <ButtonContainer>
            <ButtonWrapper>
              <Button
                variant="primary"
                size="medium"
                width="120px"
                data-testid="non-employee-password-submit"
                disabled={password === '' || confirmPassword === '' || !!error}
                loading={false}
                onClick={async () => {
                  await createPassword(
                    password,
                    confirmPassword,
                    error,
                    token,
                    setSubmitted,
                  );
                }}
              >
                Submit
              </Button>
            </ButtonWrapper>
          </ButtonContainer>
        </ShadowContainer>
      )}
      {submitted && (
        <Submitted data-testid="non-employee-submitted">
          <p>Thank you, your password has been submitted.</p>
          <p>
            You can now login with your email (also known as your Vault ID)
            <br /> and view your reports{' '}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href={`/${slug}/open-reporting`}
            >
              here
            </Link>
            .
          </p>
        </Submitted>
      )}
      <FooterContainer>
        <Footer id="desktop-powered-by-vault" />
      </FooterContainer>
    </Container>
  );
};
